import React, { useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';

import { yupResolver } from '@hookform/resolvers/yup';
import { Drawer, Box, Typography, TextField } from '@mui/material';

import { CancelButton } from 'src/components/common/buttons/CancelButton';
import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import {
	IEditProfileDrawerProps,
	IUserProfileState,
	IProfileFormData,
} from 'src/features/profile/domain/types';
import { EditProfileValidationSchema } from 'src/features/profile/domain/validation';
import { USER_PROFILE_LOADING_KEY } from 'src/features/profile/store/userProfileThunks';
import {
	getUserProfile,
	updateUserProfile,
} from 'src/features/profile/store/userProfileThunks';
import { RootState, AppDispatch } from 'src/store';

export const EditProfileDrawer: React.FC<IEditProfileDrawerProps> = ({
	open,
	onClose,
}) => {
	const dispatch = useDispatch<AppDispatch>();
	const isLoading = useSelector(
		(state: { loading: ILoadingState }) =>
			state.loading.loadingStates[USER_PROFILE_LOADING_KEY],
	);
	const { profile } = useSelector(
		(state: RootState) => state.user_profile as IUserProfileState,
	);
	const defaultData = useMemo(
		() => ({
			first_name: profile?.first_name || '',
			last_name: profile?.last_name || '',
		}),
		[profile?.first_name, profile?.last_name],
	);

	const {
		control,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm<IProfileFormData>({
		resolver: yupResolver(EditProfileValidationSchema),
		defaultValues: defaultData,
	});

	const handleUpdateProfile = async (data: IProfileFormData) => {
		if (JSON.stringify(data) === JSON.stringify(defaultData)) {
			onClose();
			return;
		}

		dispatch(
			updateUserProfile({
				request: data,
				onSuccess: () => {
					onClose();
					snackbar('Profile updated successfully', 'success');
					dispatch(getUserProfile({}));
				},
			}),
		);
	};

	useEffect(() => {
		if (open) {
			reset(defaultData);
		}
	}, [open, reset, defaultData]);

	if (!open) return null;

	return (
		<Drawer
			anchor='right'
			open={open}
			onClose={onClose}
			sx={{
				'& .MuiDrawer-paper': {
					width: '40%',
					boxSizing: 'border-box',
					padding: '32px',
					'@media (max-width: 600px)': {
						width: '100%',
					},
				},
			}}
		>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					mb: 3,
				}}
			>
				<Typography variant='h6' component='h2'>
					Edit User
				</Typography>
			</Box>

			<form onSubmit={handleSubmit(handleUpdateProfile)}>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
					<Controller
						name='first_name'
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								label='First Name'
								variant='outlined'
								fullWidth
								error={!!errors.first_name}
								helperText={errors.first_name?.message}
							/>
						)}
					/>
					<Controller
						name='last_name'
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								label='Last Name'
								variant='outlined'
								fullWidth
								error={!!errors.last_name}
								helperText={errors.last_name?.message}
							/>
						)}
					/>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 2,
							mt: 1,
						}}
					>
						<PrimaryButton isLoading={isLoading} type='submit'>
							Save Changes
						</PrimaryButton>
						<CancelButton onClick={onClose}>Cancel</CancelButton>
					</Box>
				</Box>
			</form>
		</Drawer>
	);
};
